// src/pages/StorePage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Loader from '../components/common/Loader';
import FiltersSidebar from '../components/store/FiltersSidebar';
import ProductCard from '../components/store/ProductCard';
import ProductGrid from '../components/store/ProductGrid';
import Pagination from '../components/store/Pagination';
import { apiClient } from '../auth/api';
import { useStoreState } from '../contexts/StoreStateContext';

const EXCLUDED_CATEGORY_IDS = new Set([416, 418, 419, 420, 422, 424, 425, 426, 427]);
const EXCLUDED_TEAM_IDS = new Set([5, 6]); // 5=San Antonio Spurs, 6=New York Knicks
const isBasketballCategory = (category) => {
  const id = Number(category?.id);
  if (EXCLUDED_CATEGORY_IDS.has(id)) return true;
  const name = String(category?.name || category?.display_name || '').toLowerCase();
  const slug = String(category?.slug || '').toLowerCase();
  return name.includes('basketball') || slug.includes('basketball');
};

function parsePriceInput(value) {
  const numValue = parseFloat(String(value).replace(/[^\d.]/g, ''));
  return isNaN(numValue) ? 0 : Math.round(numValue * 100);
}

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/**
 * Custom hook to fetch products with shuffled sorting by default
 */
function useProducts(limit, paramsIn, paginationOptions = {}, currentPage = 1) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          expand_colors: 1,
          limit: limit,
        };

        // Only use shuffle_seed when no category filtering is applied (normal store loading)
        if (!paramsIn?.categoryId) {
          params.shuffle_seed = paginationOptions.shuffleSeed || new Date().toISOString().split('T')[0];
        }

        // Apply all filters - backend now handles all filtering with shuffled results
        if (paramsIn?.query) params.search = paramsIn.query;
        if (paramsIn?.vendorId) params.vendor_id = paramsIn.vendorId;
        if (paramsIn?.categoryId != null) params.category_id = paramsIn.categoryId;
        if (paramsIn?.playerIds && paramsIn.playerIds.length > 0) {
          params.player_ids = paramsIn.playerIds.join(',');
        }
        if (paramsIn?.characterIds && paramsIn.characterIds.length > 0) {
          params.character_ids = paramsIn.characterIds.join(',');
        }
        // Note: series filtering is handled separately via /products/by-series endpoint
        if (paramsIn?.priceMin != null) params.price_min = paramsIn.priceMin;
        if (paramsIn?.priceMax != null) params.price_max = paramsIn.priceMax;

        // Cursor pagination for shuffled results
        if (paginationOptions.lastKey && paginationOptions.lastId) {
          params.last_key = paginationOptions.lastKey;
          params.last_id = paginationOptions.lastId;
        } else if (paginationOptions.beforeKey && paginationOptions.beforeId) {
          params.before_key = paginationOptions.beforeKey;
          params.before_id = paginationOptions.beforeId;
        }

        let data;
        
        // If series are selected, use series-specific endpoint
        if (paramsIn?.seriesIds && paramsIn.seriesIds.length > 0) {
          // For series filtering, we need to get products from all selected series
          const allSeriesProducts = [];
          const productIdSet = new Set();
          
          for (const seriesId of paramsIn.seriesIds) {
            const seriesResponse = await apiClient.get(`/products/by-series/${seriesId}/characters-products`);
            const seriesData = seriesResponse.data;
            
            if (seriesData && seriesData.characters) {
              seriesData.characters.forEach(charData => {
                if (charData.items) {
                  charData.items.forEach(product => {
                    // Apply character filtering if specified
                    if (paramsIn?.characterIds && paramsIn.characterIds.length > 0) {
                      if (!paramsIn.characterIds.includes(charData.character.id)) {
                        return; // Skip this product if character not selected
                      }
                    }
                    
                    // Avoid duplicate products
                    if (!productIdSet.has(product.id)) {
                      productIdSet.add(product.id);
                      // Add character information to the product
                      allSeriesProducts.push({
                        ...product,
                        character: charData.character
                      });
                    }
                  });
                }
              });
            }
          }
          
          // Apply other filters on the series products
          let filteredProducts = allSeriesProducts;
          
          // Apply search filter
          if (paramsIn?.query) {
            const searchTerm = paramsIn.query.toLowerCase();
            filteredProducts = filteredProducts.filter(p => 
              p.name?.toLowerCase().includes(searchTerm) ||
              p.display_name?.toLowerCase().includes(searchTerm)
            );
          }
          
          // Apply price filters
          if (paramsIn?.priceMin != null) {
            filteredProducts = filteredProducts.filter(p => (p.price_cents || 0) >= paramsIn.priceMin);
          }
          if (paramsIn?.priceMax != null) {
            filteredProducts = filteredProducts.filter(p => (p.price_cents || 0) <= paramsIn.priceMax);
          }
          
          // Apply vendor filter
          if (paramsIn?.vendorId) {
            filteredProducts = filteredProducts.filter(p => String(p.vendor_id) === String(paramsIn.vendorId));
          }
          
          // Apply category filter
          if (paramsIn?.categoryId != null) {
            filteredProducts = filteredProducts.filter(p => 
              p.categories && p.categories.includes(paramsIn.categoryId)
            );
          }
          
          // Apply pagination for series products using page number
          const startIndex = Math.max(0, (currentPage - 1) * limit);
          const endIndex = Math.min(startIndex + limit, filteredProducts.length);
          const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
          
          data = {
            items: paginatedProducts,
            total: filteredProducts.length
          };
        } else {
          // Use regular products endpoint for non-series filtering
          data = (await apiClient.get('/products', { params })).data;
        }

        const items = (data.items || []).map(p => ({
          id: p.product_id ?? p.id,
          variantId: p.variant_id ?? null,
          name: p.name,
          vendorId: p.vendor_id,
          price: p.price_cents ?? 0,
          currency: p.currency || 'USD',
          // Prefer nested primary image when available; fallback to legacy image_url
          imageUrl: (p.images && p.images.primary && p.images.primary.image_url) ? p.images.primary.image_url : (p.image_url || null),
          brand: p.brand || '',
          categories: Array.isArray(p.categories) ? p.categories : [],
          players: Array.isArray(p.players) ? p.players : [],
          color: p.color || null,
          sizes: Array.isArray(p.sizes) ? p.sizes : [],
          displayName: p.display_name,
          productUrl: p.product_url,
          description: p.description,
          stock: p.stock || 0,
          images: p.images || null,
          // Enrich with linked character or player when provided by backend
          character: p.character || (Array.isArray(p.character_links) && p.character_links.length > 0 ? p.character_links[0] : null),
          player: p.player || (Array.isArray(p.player_links) && p.player_links.length > 0 ? p.player_links[0] : null),
        }));

        if (!cancelled) {
          const filteredItems = items.filter(p => {
            const ids = Array.isArray(p.categories) ? p.categories : [];
            return !ids.some(id => EXCLUDED_CATEGORY_IDS.has(Number(id)));
          });
          setProducts(filteredItems);
          // Backend totals may include excluded categories; keep server total when present.
          setTotal((data.total != null ? data.total : filteredItems.length));
          setMeta({
            shuffleSeed: data.shuffle_seed,
            lastKey: data.last_key,
            lastId: data.last_id,
            prevKey: data.prev_key,
            prevId: data.prev_id,
          });
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    limit,
    paramsIn?.query,
    paramsIn?.vendorId,
    paramsIn?.categoryId,
    JSON.stringify(paramsIn?.playerIds || []),
    JSON.stringify(paramsIn?.characterIds || []),
    JSON.stringify(paramsIn?.seriesIds || []),
    paramsIn?.priceMin,
    paramsIn?.priceMax,
    currentPage,
    paginationOptions.shuffleSeed,
    paginationOptions.lastKey,
    paginationOptions.lastId,
    paginationOptions.beforeKey,
    paginationOptions.beforeId,
  ]);

  return { loading, error, products, total, meta };
}

function useVendors() {
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/vendors', { params: { limit: 200, offset: 0, has_products: true } });
        const items = (data.items || data || []).map(v => ({ id: v.id, name: v.name }));
        if (!cancelled) setVendors(items);
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return vendors;
}

function useCategories() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/categories', { params: { limit: 100, offset: 0 } });
        const items = data.items || [];
        if (!cancelled) setCategories(items);
      } catch (e) {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return categories;
}

function useTeams() {
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/teams', { params: { limit: 50, offset: 0 } });
        const items = (data.items || []).filter(t => !EXCLUDED_TEAM_IDS.has(Number(t?.id)));
        if (!cancelled) setTeams(items);
      } catch (e) {
        if (!cancelled) setTeams([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return teams;
}


function StorePage() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const { savedState, saveState, shouldRestore } = useStoreState();

  const pageSize = 12;

  // Default filter values
  const defaultFilters = {
    query: '',
    minPrice: '0',
    maxPrice: '0',
    vendorIds: [],
    teamIds: [],
    seriesIds: [],
    characterIds: [],
    categoryId: null,
    subcategoryIds: [],
    playerIds: [],
    viewMode: 'grid',
    page: 1,
    shuffleSeed: new Date().toISOString().split('T')[0],
    cursorHistory: {}
  };

  // Initialize from saved state if available and should restore, otherwise from URL or defaults
  const getInitialState = () => {
    // Try to load from sessionStorage directly if context hasn't loaded yet
    let stateToRestore = savedState;
    if (!stateToRestore) {
      try {
        const stored = sessionStorage.getItem('store:state:v1');
        if (stored) {
          stateToRestore = JSON.parse(stored);
        }
      } catch (e) {
        console.error('[StorePage] Failed to load from sessionStorage:', e);
      }
    }
    
    if (shouldRestore && stateToRestore) {
      return {
        query: stateToRestore.query ?? defaultFilters.query,
        minPrice: stateToRestore.minPrice ?? defaultFilters.minPrice,
        maxPrice: stateToRestore.maxPrice ?? defaultFilters.maxPrice,
        vendorIds: stateToRestore.vendorIds ?? defaultFilters.vendorIds,
        teamIds: stateToRestore.teamIds ?? defaultFilters.teamIds,
        seriesIds: stateToRestore.seriesIds ?? defaultFilters.seriesIds,
        characterIds: stateToRestore.characterIds ?? defaultFilters.characterIds,
        selectedCategoryId: stateToRestore.selectedCategoryId ?? defaultFilters.categoryId,
        subcategoryIds: stateToRestore.subcategoryIds ?? defaultFilters.subcategoryIds,
        playerIds: stateToRestore.playerIds ?? defaultFilters.playerIds,
        viewMode: stateToRestore.viewMode ?? defaultFilters.viewMode,
        page: stateToRestore.page ?? defaultFilters.page,
        shuffleSeed: stateToRestore.shuffleSeed ?? defaultFilters.shuffleSeed,
        cursorHistory: stateToRestore.cursorHistory ?? defaultFilters.cursorHistory,
      };
    }
    
    // Initialize from URL params
    const pageFromUrl = Number(urlParams.get('page') || '1');
    const initialQuery = urlParams.get('q') ?? defaultFilters.query;
    const initialMin = urlParams.get('min') ?? defaultFilters.minPrice;
    const initialMax = urlParams.get('max') ?? defaultFilters.maxPrice;
    const initialVendor = urlParams.get('vendor') ?? '';
    const initialCategory = urlParams.get('cat');
    const initialSubcats = (urlParams.get('sub') ?? '')
      .split(',')
      .filter(Boolean)
      .map(v => Number(v));
    const initialPlayers = (urlParams.get('players') ?? '')
      .split(',')
      .filter(Boolean)
      .map(v => Number(v));
    const initialView = urlParams.get('view') || defaultFilters.viewMode;

    return {
      query: initialQuery,
      minPrice: initialMin,
      maxPrice: initialMax,
      vendorIds: initialVendor ? [String(initialVendor)] : [],
      teamIds: [],
      seriesIds: [],
      characterIds: [],
      selectedCategoryId: initialCategory ? Number(initialCategory) : null,
      subcategoryIds: initialSubcats,
      playerIds: initialPlayers,
      viewMode: initialView === 'list' ? 'list' : 'grid',
      page: pageFromUrl,
      shuffleSeed: defaultFilters.shuffleSeed,
      cursorHistory: defaultFilters.cursorHistory,
    };
  };

  const initialState = getInitialState();
  const [viewMode, setViewMode] = useState(initialState.viewMode);
  const [minPrice, setMinPrice] = useState(initialState.minPrice);
  const [maxPrice, setMaxPrice] = useState(initialState.maxPrice);
  const [vendorIds, setVendorIds] = useState(initialState.vendorIds || []);
  const [teamIds, setTeamIds] = useState(initialState.teamIds);
  const [seriesIds, setSeriesIds] = useState(initialState.seriesIds);
  const [characterIds, setCharacterIds] = useState(initialState.characterIds);
  const [query, setQuery] = useState(initialState.query);
  const [selectedCategoryId, setSelectedCategoryId] = useState(initialState.selectedCategoryId);
  const [subcategoryIds, setSubcategoryIds] = useState(initialState.subcategoryIds);
  const [playerIds, setPlayerIds] = useState(initialState.playerIds);
  const [page, setPage] = useState(initialState.page);
  const [shuffleSeed] = useState(initialState.shuffleSeed);
  const [cursorHistory, setCursorHistory] = useState(initialState.cursorHistory);
  
  // Keep a ref to the latest cursor history to avoid race conditions
  const cursorHistoryRef = React.useRef(cursorHistory);
  React.useEffect(() => {
    cursorHistoryRef.current = cursorHistory;
  }, [cursorHistory]);

  // Save state whenever filters or pagination change
  useEffect(() => {
    const currentState = {
      query,
      minPrice,
      maxPrice,
      vendorIds,
      teamIds,
      seriesIds,
      characterIds,
      selectedCategoryId,
      subcategoryIds,
      playerIds,
      viewMode,
      page,
      shuffleSeed,
      cursorHistory,
    };
    saveState(currentState);
  }, [query, minPrice, maxPrice, vendorIds, teamIds, seriesIds, characterIds, selectedCategoryId, subcategoryIds, playerIds, viewMode, page, shuffleSeed, cursorHistory, saveState]);

  // Disallow Basketball categories in store.
  useEffect(() => {
    if (selectedCategoryId != null && EXCLUDED_CATEGORY_IDS.has(Number(selectedCategoryId))) {
      setSelectedCategoryId(null);
      setSubcategoryIds([]);
      setPage(1);
      setCursorHistory({});
      return;
    }
    if (Array.isArray(subcategoryIds) && subcategoryIds.length > 0) {
      const next = subcategoryIds.filter(id => !EXCLUDED_CATEGORY_IDS.has(Number(id)));
      if (next.length !== subcategoryIds.length) {
        setSubcategoryIds(next);
        setPage(1);
        setCursorHistory({});
      }
    }
  }, [selectedCategoryId, subcategoryIds]);

  // Disallow selecting excluded teams in store filters.
  useEffect(() => {
    if (!Array.isArray(teamIds) || teamIds.length === 0) return;
    const next = teamIds.filter(id => !EXCLUDED_TEAM_IDS.has(Number(id)));
    if (next.length !== teamIds.length) {
      setTeamIds(next);
      setPlayerIds([]);
      setPage(1);
      setCursorHistory({});
    }
  }, [teamIds]);

  // Debounced filters for API calls
  const dQuery = useDebounced(query);
  const dVendorId = useDebounced(vendorIds && vendorIds[0] ? String(vendorIds[0]) : null);
  const dCategoryId = useDebounced(
    // If subcategories are selected, use subcategory ID for filtering
    Array.isArray(subcategoryIds) && subcategoryIds.length > 0 
      ? Number(subcategoryIds[0]) 
      // If parent category is selected, use parent category ID for filtering
      : selectedCategoryId 
        ? Number(selectedCategoryId) 
        : null
  );
  const dPlayerIds = useDebounced(playerIds);
  const dCharacterIds = useDebounced(characterIds);
  const dSeriesIds = useDebounced(seriesIds);
  const dMin = useDebounced(parsePriceInput(minPrice));
  const dMax = useDebounced(Number(maxPrice) > 0 ? parsePriceInput(maxPrice) : null);

  const hasActiveFilters = useMemo(() => {
    return (
      query.trim() !== '' ||
      Number(minPrice) > 0 ||
      Number(maxPrice) > 0 ||
      teamIds.length > 0 ||
      seriesIds.length > 0 ||
      characterIds.length > 0 ||
      playerIds.length > 0 ||
      (subcategoryIds && subcategoryIds.length > 0)
    );
  }, [query, minPrice, maxPrice, vendorIds, teamIds, seriesIds, characterIds, playerIds, subcategoryIds]);

  // Determine pagination tokens for current page
  const getPaginationTokens = () => {
    if (page === 1) {
      return { shuffleSeed };
    }

    // Check if we have a stored cursor for this specific page
    // This is used when restoring state from product details
    const currentPageCursor = cursorHistory[page];
    if (currentPageCursor && currentPageCursor.startKey && currentPageCursor.startId) {
      return {
        shuffleSeed,
        lastKey: currentPageCursor.startKey,
        lastId: currentPageCursor.startId
      };
    }

    // Otherwise, use the end cursor from the previous page
    const prevPageData = cursorHistory[page - 1];
    if (prevPageData && prevPageData.lastKey && prevPageData.lastId) {
      return {
        shuffleSeed,
        lastKey: prevPageData.lastKey,
        lastId: prevPageData.lastId
      };
    }

    return { shuffleSeed };
  };

  // Fetch products with shuffled sorting by default
  const { loading, error, products, total, meta } = useProducts(
    pageSize,
    {
      query: dQuery,
      vendorId: dVendorId,
      categoryId: dCategoryId,
      playerIds: dPlayerIds,
      characterIds: dCharacterIds,
      seriesIds: dSeriesIds,
      priceMin: dMin,
      priceMax: dMax,
    },
    getPaginationTokens(),
    page
  );

  // Store cursor history when new data arrives
  useEffect(() => {
    if (meta.lastKey && meta.lastId) {
      setCursorHistory(prev => {
        const updated = { ...prev };
        
        // Store the ending cursor for this page (used to fetch next page)
        updated[page] = {
          lastKey: meta.lastKey,
          lastId: meta.lastId,
          prevKey: meta.prevKey,
          prevId: meta.prevId,
        };
        
        // Store the starting cursor for the next page
        // This allows us to directly fetch page N+1 when restoring state
        if (page + 1) {
          updated[page + 1] = {
            ...updated[page + 1],
            startKey: meta.lastKey,
            startId: meta.lastId,
          };
        }
        
        return updated;
      });
    }
  }, [meta, page]);

  const vendors = useVendors();
  const teams = useTeams();
  const categoriesTop = useCategories();
  const visibleCategoriesTop = useMemo(() => {
    const clean = (cats) => {
      if (!Array.isArray(cats)) return [];
      return cats
        .filter(c => !isBasketballCategory(c))
        .map(c => ({ ...c, subcategories: clean(c.subcategories || []) }));
    };
    return clean(categoriesTop);
  }, [categoriesTop]);

  const vendorById = useMemo(() => Object.fromEntries(vendors.map(v => [v.id, v.name])), [vendors]);

  // Update URL params when filters change (preserve while in store)
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('view', viewMode);
    if ((query || '').trim().length > 0) params.set('q', query.trim());
    if (Number(minPrice) > 0) params.set('min', String(minPrice));
    if (Number(maxPrice) > 0) params.set('max', String(maxPrice));
    if (vendorIds && vendorIds[0]) params.set('vendor', String(vendorIds[0]));
    if (selectedCategoryId != null) params.set('cat', String(selectedCategoryId));
    if (Array.isArray(subcategoryIds) && subcategoryIds.length > 0) {
      params.set('sub', subcategoryIds.join(','));
    }
    if (Array.isArray(playerIds) && playerIds.length > 0) {
      params.set('players', playerIds.join(','));
    }
    setUrlParams(params);
  }, [
    page,
    viewMode,
    query,
    minPrice,
    maxPrice,
    vendorIds,
    teamIds,
    selectedCategoryId,
    subcategoryIds,
    playerIds,
    setUrlParams,
  ]);

  // Reset to first page and clear cursor history when filters change
  const isInitialMount = React.useRef(true);
  const isRestoring = React.useRef(shouldRestore);
  
  useEffect(() => {
    // Update restoration flag when shouldRestore changes
    isRestoring.current = shouldRestore;
  }, [shouldRestore]);
  
  useEffect(() => {
    // Skip on initial mount to allow state restoration
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Skip if we're currently restoring state
    if (isRestoring.current) {
      isRestoring.current = false; // Reset flag after first skip
      return;
    }
    setPage(1);
    setCursorHistory({});
  }, [vendorIds, teamIds, seriesIds, characterIds, query, minPrice, maxPrice, selectedCategoryId, subcategoryIds, playerIds]);

  // Mark that at least one load succeeded
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handlePageChange = (newPage) => {
    setPage(newPage);
    // Smooth scroll to top
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const handleResetFilters = () => {
    setMinPrice(defaultFilters.minPrice);
    setMaxPrice(defaultFilters.maxPrice);
    setVendorIds(defaultFilters.vendorIds);
    setTeamIds(defaultFilters.teamIds);
    setSeriesIds(defaultFilters.seriesIds);
    setCharacterIds(defaultFilters.characterIds);
    setQuery(defaultFilters.query);
    setSelectedCategoryId(defaultFilters.categoryId);
    setSubcategoryIds(defaultFilters.subcategoryIds);
    setPlayerIds(defaultFilters.playerIds);
    setPage(1);
    setCursorHistory({});

    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('view', 'grid');
    setUrlParams(params);
  };

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />
      {(loading && !hasLoadedOnce) ? (
        <main className="flex-1 flex items-center justify-center">
          <Loader label="Loading products" />
        </main>
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row gap-6">
            <FiltersSidebar
              query={query}
              onQueryChange={setQuery}
              minPrice={minPrice}
              maxPrice={maxPrice}
              onMinPriceChange={v => setMinPrice(v)}
              onMaxPriceChange={v => setMaxPrice(v)}
              categoriesTop={visibleCategoriesTop}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={(id) => { setSelectedCategoryId(id); setSubcategoryIds([]); setPage(1); }}
              subcategoryIds={subcategoryIds}
              onToggleSubcategory={(id) => { setSubcategoryIds([id]); setPage(1); }}
              vendors={vendors}
              vendorIds={vendorIds}
              onSelectVendor={val => setVendorIds(val ? [String(val)] : [])}
              onToggleVendor={id =>
                setVendorIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
              }
              onPlayerFilterChange={ids => setPlayerIds(ids.map(Number))}
              playerIds={playerIds}
              onTeamFilterChange={ids => {
                const newTeamIds = ids.map(Number);
                setTeamIds(newTeamIds);
                
                // Clear selected players when changing teams
                setPlayerIds([]);
                
                // Auto-select vendors based on team names
                if (newTeamIds.length > 0) {
                  const selectedTeams = teams.filter(team => newTeamIds.includes(team.id));
                  const matchingVendors = [];
                  
                  selectedTeams.forEach(team => {
                    // Find vendor by team name (not display_name)
                    const matchingVendor = vendors.find(vendor => 
                      vendor.name.toLowerCase() === team.name.toLowerCase()
                    );
                    if (matchingVendor && !matchingVendors.includes(matchingVendor.id)) {
                      matchingVendors.push(matchingVendor.id);
                    }
                  });
                  
                  setVendorIds(matchingVendors.map(String));
                } else {
                  // Clear vendor selection when no teams selected
                  setVendorIds([]);
                }
              }}
              teamIds={teamIds}
              onSeriesFilterChange={ids => setSeriesIds(ids.map(Number))}
              seriesIds={seriesIds}
              onCharacterFilterChange={ids => setCharacterIds(ids.map(Number))}
              characterIds={characterIds}
              onReset={handleResetFilters}
              error={error}
            />

            <section className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold">Store</h1>
                <div className="flex items-center gap-3 text-sm">
                  <div className="hidden sm:block text-white/70">
                    {`${products.length} of ${total} variant${total !== 1 ? 's' : ''}`}
                  </div>
                  {loading && (
                    <div className="inline-flex items-center gap-2 text-white/70">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Loading...
                    </div>
                  )}
                  <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full border border-white/10 bg-white/5 backdrop-blur-md">
                    <button
                      className={`text-xs px-2 py-1 rounded-full ${viewMode === 'grid'
                        ? 'bg-cyan-500/20 border border-cyan-400/30 text-cyan-100'
                        : 'text-white/80 hover:text-white'
                        }`}
                      onClick={() => setViewMode('grid')}
                    >
                      Grid
                    </button>
                    <button
                      className={`text-xs px-2 py-1 rounded-full ${viewMode === 'list'
                        ? 'bg-fuchsia-500/20 border border-fuchsia-400/30 text-fuchsia-100'
                        : 'text-white/80 hover:text-white'
                        }`}
                      onClick={() => setViewMode('list')}
                    >
                      List
                    </button>
                  </div>
                </div>
              </div>

              <div className="min-h-[520px] w-full">
                {products.length === 0 ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-5 text-center rounded-xl bg-white/5 px-6 min-h-[520px]">
                    <div className="text-xl font-semibold text-white/90">
                      {loading ? 'Loading products...' : 'No products match your filters'}
                    </div>
                    <div className="text-base text-white/70 max-w-4xl">
                      {hasActiveFilters
                        ? 'Try adjusting your filters or clear them to see more products.'
                        : 'No products are currently available.'}
                    </div>
                    {hasActiveFilters && (
                      <button
                        className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/10 hover:bg-white/20 transition"
                        onClick={handleResetFilters}
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {viewMode === 'grid' ? (
                      <ProductGrid>
                        {products.map((p, i) => (
                          <ProductCard
                            key={p.id + '-' + (p.variantId || 'base') + '-' + (p.color || 'nc')}
                            product={p}
                            vendorName={vendorById[p.vendorId]}
                            onClick={() => {
                              // Save current state before navigating (use ref to get latest cursor history)
                              saveState({
                                query,
                                minPrice,
                                maxPrice,
                                vendorIds,
                                teamIds,
                                seriesIds,
                                characterIds,
                                selectedCategoryId,
                                subcategoryIds,
                                playerIds,
                                viewMode,
                                page,
                                shuffleSeed,
                                cursorHistory: cursorHistoryRef.current,
                              });
                              navigate(`/store/${p.id}`, {
                                state: { 
                                  initialVariantId: p.variantId, 
                                  preferredColor: p.color,
                                  character: p.character,
                                  player: p.player,
                                },
                              });
                            }}
                            variant="grid"
                            priority={i < 8}
                          />
                        ))}
                      </ProductGrid>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {products.map((p, i) => (
                          <ProductCard
                            key={p.id + '-' + (p.variantId || 'base') + '-' + (p.color || 'nc')}
                            product={p}
                            vendorName={vendorById[p.vendorId]}
                            onClick={() => {
                              // Save current state before navigating (use ref to get latest cursor history)
                              saveState({
                                query,
                                minPrice,
                                maxPrice,
                                vendorIds,
                                teamIds,
                                seriesIds,
                                characterIds,
                                selectedCategoryId,
                                subcategoryIds,
                                playerIds,
                                viewMode,
                                page,
                                shuffleSeed,
                                cursorHistory: cursorHistoryRef.current,
                              });
                              const sp = new URLSearchParams();
                              if (p.variantId) sp.set('vid', String(p.variantId));
                              if (p.color) sp.set('color', String(p.color));
                              navigate(`/store/${p.id}${sp.toString() ? `?${sp.toString()}` : ''}`, {
                                state: { character: p.character, player: p.player }
                              });
                            }}
                            variant="list"
                            priority={i < 5}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {total > pageSize && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onChange={handlePageChange}
                  onPrev={() => {
                    setPage(p => Math.max(1, p - 1));
                  }}
                />
              )}
            </section>
          </div>
        </main>
      )}
      <Footer />
    </div>
  );
}

export default StorePage;
